import { type ConfirmationType } from '@/entities/confirmationType/@/product';
import { type HotelProduct } from '@/entities/hotel/@/product';
import { type HotelCategoryData } from '@/entities/hotelCategory/@/product';
import { type MealData } from '@/entities/meal/@/product';
import { type OperatorProduct } from '@/entities/operator/@/product';
import { type SearchCriteria } from '@/entities/searchCriteria/@/product';
import {
  type ProductSearchBaseParams,
  type ProductSearchCacheResult,
  type ProductSearchSessionParams,
} from '@/shared/api/productSearch';
import type { ProductType } from '@/shared/config/pages';
import {
  type DepartureCity,
  type Country,
  type CountryProductDTO,
  type Resort,
  type RoomGroupDTO,
  type FirstPaymentDefinitionsDTO,
  type OperatorProductDTO,
} from '@/shared/model';

export enum HotelAvailable {
  None = 0,
  All = 1,
  ByRequest = 2,
}

export interface Product {
  type: ProductType;
  id: string;
  price: number;
  oilTax: number | null;
  maxHistoricalPrice: number;
  discount: number;
  sortRate: number;
  checkInDate: string;
  departureCity: DepartureCity | null;
  country: Country | null;
  resort: Resort | null;
  hotel: HotelProduct | null;
  hotelCategory: HotelCategoryData | null;
  hotelAvailable: number;
  confirmationType: ConfirmationType | null;
  block: boolean;
  nights: { total: number; hotel: number };
  meal: MealData | null;
  operator: OperatorProduct | null;
  roomName: string;
  roomGroups: RoomGroupDTO[];
  cashbackAvailable: boolean;
  medicalInsurance: boolean;
  firstPaymentDefinitions: FirstPaymentDefinitionsDTO[];
}

/** Общая часть DTO любого продукта из ответа поиска. */
export interface ProductDTO {
  id: string;
  price: number;
}

/** Справочники для сборки продукта из DTO; `hotels` — общие инстансы `HotelProduct` (один на id). */
export interface ProductDictionaries {
  meals: Record<string, MealData>;
  hotelCategories: Record<string, HotelCategoryData>;
  departureCities: Record<string, DepartureCity>;
  countries: Record<string, Country>;
  resorts: Record<string, Resort>;
  hotels: Record<string, HotelProduct>;
  firstPaymentDefinitions: Record<string, FirstPaymentDefinitionsDTO>;
  operators: Record<string, OperatorProductDTO>;
}

/**
 * Режим запуска: `both` — cache-выборка + асинхронный поиск (по умолчанию); `sync` — только cache-выборка;
 * `async` — только асинхронный поиск с выборкой результата; `asyncWithoutProducts` — только сессия и опрос
 * статуса, без выборки продуктов («прогрев» поиска).
 */
export type ProductSearchMode = 'both' | 'sync' | 'async' | 'asyncWithoutProducts';

/**
 * `loading` — до ответа cache-выборки (в async-режимах — до старта опроса статуса);
 * `asyncLoading` — идёт опрос статуса асинхронного поиска; `finished`/`aborted` — терминальные.
 */
export type ProductSearchState = 'idle' | 'loading' | 'asyncLoading' | 'finished' | 'aborted';

/** Откуда пришла порция продуктов: cache-выборка, промежуточная или финальная выборка асинхронного поиска. */
export type ProductSearchPhase = 'sync' | 'middle' | 'final';

export type ProductSearchErrorPhase = 'criteria' | 'sync' | 'session' | 'final' | 'run';

export interface ProductSearchError {
  phase: ProductSearchErrorPhase;
  error: Error;
}

export interface ProductSearchOptions {
  mode?: ProductSearchMode;
  /** Переопределяет лимит cache-выборок (sync и final) конкретного поиска. */
  limit?: number;
  /** Финальная выборка уходит с `track=1` — api-gateway трекает именно её. */
  track?: boolean;
  /** Идентификатор поиска для трекинга (`commandUuid` запросов, `searchUuid` опроса статуса); генерируется, если не задан. */
  searchUuid?: string;
  customerUuid?: string;
  /** Дополнительные параметры хоста (sort, фильтры, abTests) — уходят как есть в cache- и session-запросы. */
  params?: Record<string, unknown>;
  withLowLimit?: boolean;
}

export interface ProductSearchRequestContext {
  tracking: { commandUuid?: string; clientUuid?: string };
  params?: Record<string, unknown>;
}

export interface ProductSearchRequest {
  cache: ProductSearchBaseParams;
  session: ProductSearchSessionParams;
  /** Групповой поиск (несколько комнат) — у туров свои эндпоинты. */
  grouped: boolean;
}

/** Пути относительно `apiGatewayUrl`; статус сессии опрашивается по `${session}/${sessionId}`. */
export interface ProductSearchEndpoints {
  cache: string;
  session: string;
}

export interface ProductSearchLimits {
  sync: number;
  final: number;
}

export interface ProductSearchProgress {
  jobsCount: number;
  jobsSuccess: number;
  operators: Record<string, string>;
}

export interface ProductSearchTimings {
  startedAt: number | null;
  /** Момент, когда впервые появились отели (cache-выборка или промежуточная порция). */
  firstResultsAt: number | null;
  finishedAt: number | null;
}

/** Изменения одной порции; накопленное состояние — в `search.products`/`search.hotels`/`search.productsByHotel`. */
export interface ProductSearchBatch<P extends Product> {
  phase: ProductSearchPhase;
  newProducts: P[];
  newHotels: HotelProduct[];
  replacedProductIds: string[];
  updatedHotelIds: number[];
}

export interface ProductSearchSnapshot<P extends Product> {
  products: P[];
  hotels: HotelProduct[];
  productsByHotel: Map<number, P[]>;
  countries: CountryProductDTO[];
  operators: OperatorProductDTO[];
}

export interface ProductSearchResult<P extends Product> extends ProductSearchSnapshot<P> {
  searchUuid: string;
  state: 'finished' | 'aborted';
  completed: boolean;
  timedOut: boolean;
  scanned: number;
  errors: ProductSearchError[];
  progress: ProductSearchProgress;
  timings: ProductSearchTimings;
}

/** Карта событий поиска; без аргумента — в терминах базового `Product`, контракт для потребителей. */
export type ProductSearchEvents<P extends Product = Product> = {
  start: { searchUuid: string; criteria: SearchCriteria; options: ProductSearchOptions };
  found: ProductSearchBatch<P>;
  cache: ProductSearchSnapshot<P>;
  syncEnd: ProductSearchSnapshot<P>;
  progress: ProductSearchProgress;
  error: ProductSearchError;
  end: ProductSearchResult<P>;
};

/**
 * Абстрактный контракт поиска продукта — то, чем пользуется UI, не зная тип продукта.
 * Его реализуют `TourSearch`/`HotelOfferSearch`/`RailwaySearch`.
 */
export interface ProductSearch {
  readonly type: ProductType;
  readonly state: ProductSearchState;
  readonly searchUuid: string | null;
  readonly products: readonly Product[];
  readonly hotels: readonly HotelProduct[];
  readonly productsByHotel: ReadonlyMap<number, readonly Product[]>;
  readonly progress: ProductSearchProgress;
  readonly timings: ProductSearchTimings;

  run(criteria: SearchCriteria, options?: ProductSearchOptions): Promise<ProductSearchResult<Product>>;
  abort(): void;

  on<K extends keyof ProductSearchEvents>(event: K, listener: (payload: ProductSearchEvents[K]) => void): () => void;
  once<K extends keyof ProductSearchEvents>(event: K, listener: (payload: ProductSearchEvents[K]) => void): () => void;
  off<K extends keyof ProductSearchEvents>(event: K, listener: (payload: ProductSearchEvents[K]) => void): void;
}

export interface ProductSearchConfig<P extends Product, DTO extends ProductDTO> {
  type: ProductType;
  sections: readonly string[];
  limits: ProductSearchLimits;
  resolveEndpoints(request: ProductSearchRequest): ProductSearchEndpoints;
  pickProducts(result: ProductSearchCacheResult): DTO[];
  createProduct(dto: DTO, dictionaries: ProductDictionaries): P;
  /** Цена для ключа «тот же продукт по той же цене»; по умолчанию `dto.price`. */
  getProductPrice?(dto: DTO): number;
  buildRequest?(criteria: SearchCriteria, context: ProductSearchRequestContext): ProductSearchRequest;
}
